import {
  clearCachedSchemaId,
  getLatestSchemaId,
} from '@common/state/modules/notification/services';
import { ThunkAction, UnknownAction } from '@reduxjs/toolkit';
import { modules } from '@veritone/glc-redux';
import {
  FALLBACK_DOCUMENT_COMMENTS_REGISTRY_ID,
  FALLBACK_REDACTION_STATUSES_REGISTRY_ID,
} from '@helpers/constants';

const {
  config: { getConfig },
} = modules;

export type Thunk<P, R = any> = (
  payload: P,
  ...args: any[]
) => ThunkAction<Promise<R | undefined>, any, unknown, UnknownAction>;

export const lookupLatestCaseSchemaId = (
  () => async (dispatch: any, getState: any) => {
    const { cbsaCaseRegistryId, caseRegistryId, apiRoot } =
      getConfig<Window['config']>(getState());
    const dataRegistryId = caseRegistryId || cbsaCaseRegistryId;

    return await getLatestSchemaId(
      dispatch,
      getState,
      dataRegistryId,
      caseRegistryId ? 'caseRegistryId' : 'cbsaCaseRegistryId',
      apiRoot
    );
  }
)();

export const lookupLatestCaseNotificationSchemaId = (
  () => async (dispatch: any, getState: any) => {
    const { caseNotificationRegistryId: dataRegistryId, apiRoot } =
      getConfig<Window['config']>(getState());

    return await getLatestSchemaId(
      dispatch,
      getState,
      dataRegistryId,
      'caseNotificationRegistryId',
      apiRoot
    );
  }
)();

export const lookupLatestMediaCommentsSchemaId = (
  () => async (dispatch: any, getState: any) => {
    const { redactMediaCommentRegistryId: dataRegistryId, apiRoot } =
      getConfig<Window['config']>(getState());

    return await getLatestSchemaId(
      dispatch,
      getState,
      dataRegistryId,
      'redactMediaCommentRegistryId',
      apiRoot
    );
  }
)();

export const lookupLatestDocumentCommentsSchemaId = (
  () => async (dispatch: any, getState: any) => {
    const {
      // TODO: eventually this should always come from config; hard-coded
      // default is a temporary fallback so the document-comments registry
      // works out-of-the-box without requiring a config redeploy (VE-21982).
      redactDocumentCommentsRegistryId:
        dataRegistryId = FALLBACK_DOCUMENT_COMMENTS_REGISTRY_ID,
      apiRoot,
    } = getConfig<Window['config']>(getState());

    return await getLatestSchemaId(
      dispatch,
      getState,
      dataRegistryId,
      'redactDocumentCommentsRegistryId',
      apiRoot
    );
  }
)();

export const lookupLatestRedactionCodesSchemaId = (
  () => async (dispatch: any, getState: any) => {
    const { redactionCodesRegistryId: dataRegistryId, apiRoot } =
      getConfig<Window['config']>(getState());

    return await getLatestSchemaId(
      dispatch,
      getState,
      dataRegistryId,
      'redactionCodesRegistryId',
      apiRoot
    );
  }
)();

/**
 * The single place the redaction-statuses registry is resolved from config.
 *
 * Both the lookup and its invalidator go through this, so the two can never
 * key the schema-id cache differently — rebuilding the same expression at a
 * call site is how a `null` config value ends up resolving to nothing on one
 * side and to the default registry on the other.
 *
 * TODO: eventually the registry id should always come from config; the
 * hard-coded default is a temporary fallback.
 */
const getRedactionStatusesRegistry = (getState: any) => {
  const {
    redactionStatusesSchemaId,
    redactionStatusesRegistryId:
      dataRegistryId = FALLBACK_REDACTION_STATUSES_REGISTRY_ID,
    apiRoot,
  } = getConfig<Window['config']>(getState());
  return { redactionStatusesSchemaId, dataRegistryId, apiRoot };
};

// TODO this needs to be moved out of cbsa! as well as the other lookups that are currently here that are not cbsa specific!
/**
 * `ignorePinnedSchemaId` skips the `redactionStatusesSchemaId` config pin and
 * goes to the registry. Callers use it when a write failed schema validation:
 * with a pin in place the retry re-resolved the same stale id, so a genuine
 * schema republish left saves broken until a config redeploy (VE-24546).
 *
 * No shipped config pins the id any more — VE-25957 removed it from all five,
 * precisely because pinning breaks saves once a newer minor version is
 * published, so the normal path already resolves the registry's
 * `publishedSchema`. This option remains for hand-pinned environments.
 */
export const lookupLatestRedactionStatusSchemaId = (
  () =>
  async (
    dispatch: any,
    getState: any,
    options?: { ignorePinnedSchemaId?: boolean }
  ) => {
    const { redactionStatusesSchemaId, dataRegistryId, apiRoot } =
      getRedactionStatusesRegistry(getState);

    if (redactionStatusesSchemaId && !options?.ignorePinnedSchemaId) {
      return redactionStatusesSchemaId;
    }

    return await getLatestSchemaId(
      dispatch,
      getState,
      dataRegistryId,
      'redactionStatusesRegistryId',
      apiRoot
    );
  }
)();

/**
 * Drop the cached redaction-statuses schema id, so the next lookup re-resolves.
 * Paired with `lookupLatestRedactionStatusSchemaId` on purpose: the cache key is
 * derived from the same config resolution the lookup used, so a change to
 * either stays consistent by construction.
 */
export const clearRedactionStatusSchemaId = (getState: any) => {
  const { dataRegistryId, apiRoot } = getRedactionStatusesRegistry(getState);
  clearCachedSchemaId(dataRegistryId, apiRoot);
};

export const lookupLatestTdoLockSchemaId = (
  () => async (dispatch: any, getState: any) => {
    const { tdoLockRegistryId: dataRegistryId, apiRoot } =
      getConfig<Window['config']>(getState());
    return await getLatestSchemaId(
      dispatch,
      getState,
      dataRegistryId,
      'tdoLockRegistryId',
      apiRoot
    );
  }
)();
